import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '../ui/icons';
import { ModerationActions } from './ModerationActions';
import { useCall } from './CallProvider';

/**
 * Actions a teacher / curator / owner can take on someone else in the call:
 * silence their mic, ban them from chat (for a while or until unmuted), or
 * remove them from the room.
 *
 * The panel is portaled to `document.body` and positioned with `fixed`
 * coordinates: call tiles clip with `overflow: hidden` (video must not spill),
 * so an in-tile menu would be cut off by the rounded frame.
 */
export function ModerationMenu({
  userId,
  userName,
}: {
  userId: string;
  userName: string;
}) {
  const call = useCall();
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!open || !btnRef.current) return;
    const place = () => {
      const rect = btnRef.current!.getBoundingClientRect();
      const panel = panelRef.current;
      const width = panel?.offsetWidth ?? 188;
      const height = panel?.offsetHeight ?? 260;
      let top = rect.top - height - 6;
      if (top < 8) top = rect.bottom + 6;
      let left = rect.right - width;
      left = Math.min(Math.max(8, left), window.innerWidth - width - 8);
      top = Math.min(Math.max(8, top), window.innerHeight - height - 8);
      setCoords({ top, left });
    };
    place();
    requestAnimationFrame(place);
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (btnRef.current?.contains(t) || panelRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!call.canModerate) return null;
  const localId = call.participants.find((p) => p.isLocal)?.identity;
  if (userId === localId) return null;

  const chatMuted = call.isUserChatMuted(userId);

  const panel =
    open &&
    createPortal(
      <div
        className="mod-menu-panel"
        role="menu"
        ref={panelRef}
        style={
          coords
            ? { position: 'fixed', top: coords.top, left: coords.left, right: 'auto', bottom: 'auto' }
            : { position: 'fixed', visibility: 'hidden' }
        }
      >
        <ModerationActions
          userId={userId}
          userName={userName}
          chatMuted={chatMuted}
          onMuteCall={(id) => void call.muteCallParticipant(id, true)}
          onUnmuteCall={(id) => void call.muteCallParticipant(id, false)}
          onMuteChat={(id, minutes) => void call.muteChat(id, minutes)}
          onUnmuteChat={(id) => void call.unmuteChat(id)}
          onKick={(id) => void call.kickCallParticipant(id)}
          onBan={(id) => void call.banFromCall(id)}
          onDone={() => setOpen(false)}
        />
      </div>,
      document.body,
    );

  return (
    <div className="mod-menu">
      <button
        ref={btnRef}
        type="button"
        className="call-bar-btn"
        aria-label={`Модерация: ${userName}`}
        aria-expanded={open}
        data-tip="Модерация"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
      >
        <Icon name="more" size={14} />
      </button>
      {panel}
    </div>
  );
}
